import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import styles from './ChatPane.module.css';

const PINNED_KEY = 'ego_pinned_sessions';

function getPinned() {
  try {
    return JSON.parse(localStorage.getItem(PINNED_KEY) || '[]');
  } catch { return []; }
}

function setPinned(ids) {
  localStorage.setItem(PINNED_KEY, JSON.stringify(ids));
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function SessionList({
  sessions,
  currentSessionId,
  onSelectSession,
  onRenameSession,
  onDeleteSession,
  onClose,
}) {
  const [pinnedIds, setPinnedIds] = useState(getPinned);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const editInputRef = useRef(null);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handle = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpenId]);

  // Focus input when editing
  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  const { pinned, recent, childrenMap } = useMemo(() => {
    const cMap = {};
    const topLevel = [];
    const sessionIds = new Set(sessions.map(s => s.id));

    for (const s of sessions) {
      if (s.parent_session_id && sessionIds.has(s.parent_session_id)) {
        if (!cMap[s.parent_session_id]) cMap[s.parent_session_id] = [];
        cMap[s.parent_session_id].push(s);
      } else {
        topLevel.push(s);
      }
    }

    const p = topLevel.filter(s => pinnedIds.includes(s.id));
    const r = topLevel.filter(s => !pinnedIds.includes(s.id));
    return { pinned: p, recent: r, childrenMap: cMap };
  }, [sessions, pinnedIds]);

  const handlePin = (id) => {
    const next = pinnedIds.includes(id)
      ? pinnedIds.filter(x => x !== id)
      : [...pinnedIds, id];
    setPinnedIds(next);
    setPinned(next);
    setMenuOpenId(null);
  };

  const handleStartRename = (s) => {
    setEditingId(s.id);
    setEditValue(s.title || '');
    setMenuOpenId(null);
  };

  const handleSaveRename = () => {
    if (editValue.trim() && editingId) {
      onRenameSession(editingId, editValue.trim());
    }
    setEditingId(null);
  };

  const handleDelete = (id) => {
    setMenuOpenId(null);
    setConfirmDeleteId(id);
  };

  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      onDeleteSession(confirmDeleteId);
      if (pinnedIds.includes(confirmDeleteId)) {
        const next = pinnedIds.filter(x => x !== confirmDeleteId);
        setPinnedIds(next);
        setPinned(next);
      }
    }
    setConfirmDeleteId(null);
  };

  const renderRow = (s, depth = 0) => {
    const isActive = s.id === currentSessionId;
    const isEditing = editingId === s.id;
    const isFork = depth > 0;

    // Strip "Fork: " prefix for indented forks — the tree makes it redundant
    let displayTitle = s.title?.slice(0, 60) || 'Untitled';
    if (isFork && displayTitle.startsWith('Fork: ')) {
      displayTitle = displayTitle.slice(6);
    }

    return (
      <div
        key={s.id}
        className={`${styles.sessionRow} ${isActive ? styles.sessionRowActive : ''}`}
        style={depth > 0 ? { paddingLeft: `${16 + depth * 20}px` } : undefined}
        onClick={() => {
          if (!isEditing) {
            onSelectSession(s.id);
            onClose();
          }
        }}
      >
        <div className={styles.sessionRowContent}>
          {isEditing ? (
            <input
              ref={editInputRef}
              className={styles.sessionEditInput}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') setEditingId(null);
              }}
              onBlur={handleSaveRename}
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <>
              <span className={styles.sessionRowTitle}>
                {isFork && <span className={styles.sessionForkIcon}>&#9666;</span>}
                {displayTitle}
              </span>
              <span className={styles.sessionRowDate}>
                {formatDate(s.updated_at || s.created_at)}
              </span>
            </>
          )}
        </div>
        {!isEditing && (
          <div className={styles.sessionKebabWrapper}>
            <button
              className={styles.sessionKebab}
              onClick={e => {
                e.stopPropagation();
                setMenuOpenId(menuOpenId === s.id ? null : s.id);
              }}
            >
              ···
            </button>
            {menuOpenId === s.id && (
              <div className={styles.sessionMenu} ref={menuRef}>
                <button
                  className={styles.sessionMenuItem}
                  onClick={e => { e.stopPropagation(); handleStartRename(s); }}
                >
                  Rename
                </button>
                <button
                  className={styles.sessionMenuItem}
                  onClick={e => { e.stopPropagation(); handlePin(s.id); }}
                >
                  {pinnedIds.includes(s.id) ? 'Unpin' : 'Pin'}
                </button>
                <button
                  className={`${styles.sessionMenuItem} ${styles.sessionMenuDanger}`}
                  onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTree = (session, depth = 0) => (
    <Fragment key={session.id}>
      {renderRow(session, depth)}
      {childrenMap[session.id]?.map(child => renderTree(child, depth + 1))}
    </Fragment>
  );

  return (
    <div className={styles.sessionList}>
      <div className={styles.sessionListScroll}>
        {sessions.length === 0 ? (
          <div className={styles.sessionListEmpty}>No conversations yet</div>
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <div className={styles.sessionDivider}>Pinned</div>
                {pinned.map(s => renderTree(s))}
              </>
            )}
            {recent.length > 0 && (
              <>
                {pinned.length > 0 && (
                  <div className={styles.sessionDivider}>Recent</div>
                )}
                {recent.map(s => renderTree(s))}
              </>
            )}
          </>
        )}
      </div>
      {confirmDeleteId && (
        <ConfirmDialog
          message="Delete this conversation?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
